import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { LocalityModalInput } from '@/src/elements/input/LocalityModalInput/LocalityModalInput'
import { RadioButton } from '@/src/elements/radio/RadioButton'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { useCommunityJoinSteps } from '@/src/features/community/ui/hooks/useCommunityJoinSteps'
import { useFraternityOnboardingMutation } from '@/src/features/fraternity/queries/useFraternityOnboardingMutation'
import { useFraternityOnboardingQuery } from '@/src/features/fraternity/queries/useFraternityOnboardingQuery'
import { FraternityOnboardingWrapper } from '@/src/features/fraternity/ui/components/FraternityOnboardingWrapper'
import type { TLocation } from '@/src/features/settings/api/types/editAccountSettings.types'
import { useSettingsMutation } from '@/src/features/settings/services/useSettingsMutation'
import { SettingsProfileButton } from '@/src/features/settings/ui/components/SettingsProfileButton/SettingsProfileButton'
import { getMeLocation } from '@/src/features/settings/utils/getMeLocation'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

type TFraternityPlaceScreenProps = TIdSlugParam & TSearchParamsParam

export const FraternityPlaceScreen = ({ idSlug, searchParams }: TFraternityPlaceScreenProps): ReactNode => {
  const { t } = useTranslate()
  const { data: me } = useMeQuery()
  const { data: onboarding } = useFraternityOnboardingQuery({ enabled: true })
  const joinSteps = useCommunityJoinSteps({ idSlug, searchParams, stepName: 'place' })
  const { goToNextStep } = joinSteps

  const [hasAskedRemote, setHasAskedRemote] = useState<boolean | null>(onboarding?.has_asked_remote ?? null)
  const [location, setLocation] = useState<TLocation | undefined>(getMeLocation(me))

  const { mutateAsync: patchOnboarding, isPending: isOnboardingSaving } = useFraternityOnboardingMutation()
  const { mutateAsync: patchSettings, isPending: isSettingsSaving } = useSettingsMutation()

  const isSaving = isOnboardingSaving || isSettingsSaving
  const city = location?.city ?? me?.city
  const canValidate = hasAskedRemote === true || (hasAskedRemote === false && Boolean(city))

  const handleLocationSubmit = async (nextLocation?: TLocation) => {
    if (!nextLocation) return
    setLocation(nextLocation)
    await patchSettings({ location: nextLocation })
  }

  const handleValidate = async () => {
    if (hasAskedRemote == null) return
    await patchOnboarding({ has_asked_remote: hasAskedRemote })
    goToNextStep()
  }

  return (
    <FraternityOnboardingWrapper
      joinSteps={joinSteps}
      currentStep="place"
      title={<FormattedMessage id="fraternity.place.title" />}
      subtitle={<FormattedMessage id="fraternity.place.hint" />}
      footer={
        <Button
          colors="primary"
          variant="solid"
          fullWidth
          isDisabled={!canValidate}
          isLoading={isSaving}
          onPress={handleValidate}
        >
          <FormattedMessage id="word.continue" />
        </Button>
      }
    >
      <View className="gap-3">
        <RadioButton
          icon={ICON.VIDEO_RECORDER}
          selected={hasAskedRemote === true}
          label={t('fraternity.place.remote')}
          description={t('fraternity.place.remote-description')}
          onPress={() => setHasAskedRemote(true)}
        />
        <RadioButton
          icon={ICON.MARKER_PIN}
          selected={hasAskedRemote === false}
          label={t('fraternity.place.nearby')}
          description={t('fraternity.place.nearby-description')}
          onPress={() => setHasAskedRemote(false)}
        />
        {hasAskedRemote === false && (
          <LocalityModalInput
            title={t('fraternity.place.city-label')}
            placeholder={t('user.settings.click-to-fill')}
            inputTestId={TEST_ID.SETTINGS.LOCATION.INPUT}
            onUncaughtSubmit={handleLocationSubmit}
            renderTrigger={({ onTrigger }) => (
              <SettingsProfileButton
                onPress={onTrigger}
                label={t('fraternity.place.city-label')}
                placeholder={t('user.settings.click-to-fill')}
                value={city ?? undefined}
              />
            )}
          />
        )}
      </View>
    </FraternityOnboardingWrapper>
  )
}
